import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Copy, Download, Loader2, X } from 'lucide-react';
import clsx from 'clsx';

import { adbApi } from '@/lib/api';
import { base64ToBlob, copyImageToClipboard, downloadBlob } from '@/lib/blob';

/**
 * Icon-picker modal (§3 of layout-name-apps-drop plan).
 *
 *   ┌─────────────────────────────────────────────────────────────┐
 *   │ com.example.app · 抖音                              192×192 │
 *   │ ┌───────────────────┐ ┌─────────────────────────────────┐  │
 *   │ │                   │ │ [主图 192] [192] [144] [96] [48]│  │
 *   │ │   大图预览          │ │ 主图始终首格，剩下按密度排序          │  │
 *   │ │   (max 192px)     │ │                                  │  │
 *   │ │                   │ │                                  │  │
 *   │ └───────────────────┘ └─────────────────────────────────┘  │
 *   │ res/mipmap-xxxhdpi-v4/ic_launcher.png                       │
 *   │ [复制图片] [下载 PNG]                                [关闭]   │
 *   └─────────────────────────────────────────────────────────────┘
 *
 * Two data sources, intentionally:
 *
 *  - `app.icon` cache (shared with the Apps list — same queryKey, no
 *    extra request) gives us the **primary** launcher PNG synchronously
 *    the moment the modal opens. Without this the user would stare at
 *    a spinner while `app.icon.list` pulls the APK off the device.
 *
 *  - `app.icon.list` then enumerates every density bucket the manifest
 *    declares; when it returns we merge those candidates into the grid.
 *    The backend already filters to manifest-only entries (skipping
 *    the broad `res/mipmap-*` scan) when the manifest is resolvable, so
 *    "many similar icons" is no longer a problem at this layer.
 */

interface IconCandidate {
  resPath: string;
  density: string;
  width: number;
  height: number;
  bytes: number;
  pngBase64: string;
  // 'primary' — synthetic candidate sourced from the `app.icon` cache
  //   (i.e. the icon the Apps list is already showing). Always sorted
  //   to the very first grid slot so the modal has something to show
  //   immediately when it opens.
  // 'manifest' — declared by AndroidManifest.xml's application/@icon
  //   (resolved through resources.arsc). The backend's preferred set.
  // 'scan' — fallback name-pattern hit; only appears when the backend
  //   couldn't resolve any manifest icon.
  source?: 'primary' | 'manifest' | 'scan';
}

interface IconListResult {
  icons: IconCandidate[];
  primaryResPath?: string;
  cached: boolean;
}

/** Shape of the `app.icon` cache entry produced by the Apps list. */
interface AppIconCached {
  label?: string;
  labelZh?: string;
  iconPng?: string;
  versionName?: string;
  cached?: boolean;
}

/**
 * Synthetic resource path for the primary candidate. Distinct from any
 * real `res/...` path so the candidate dedupe never confuses it with
 * a manifest entry.
 */
const PRIMARY_RES_PATH = '__primary__';

interface Props {
  serial: string;
  package: string;
  /** Display label (zh-CN preferred) for the modal header. */
  label?: string;
  apkPath?: string;
  versionCode?: number;
  onClose: () => void;
  onToast: (msg: string) => void;
}

export function IconPickerModal({
  serial,
  package: pkg,
  label,
  apkPath,
  versionCode,
  onClose,
  onToast,
}: Props) {
  const qc = useQueryClient();
  const [selectedPath, setSelectedPath] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Read the Apps-list cache so the modal can render the same icon the
  // user just clicked on, with zero network. Note: cache key MUST
  // match the one used in `AppCard.iconQuery` exactly, including the
  // `versionCode` slot (omit it from the key if the caller didn't
  // pass one — `undefined` keys round-trip correctly through tanstack
  // query's structural equality).
  const cachedPrimary = useMemo<AppIconCached | undefined>(() => {
    return qc.getQueryData<AppIconCached>(['app.icon', serial, pkg, versionCode]);
  }, [qc, serial, pkg, versionCode]);

  const primary = useMemo<IconCandidate | null>(() => {
    if (!cachedPrimary?.iconPng) return null;
    // We don't know the primary's pixel size without parsing IHDR;
    // synthesise a zero so the UI can render "—" rather than a wrong
    // dimension. The real density-tagged candidates from app.icon.list
    // (when they arrive) carry accurate width/height.
    return {
      resPath: PRIMARY_RES_PATH,
      density: '',
      width: 0,
      height: 0,
      bytes: Math.floor((cachedPrimary.iconPng.length * 3) / 4),
      pngBase64: cachedPrimary.iconPng,
      source: 'primary',
    };
  }, [cachedPrimary]);

  const list = useQuery({
    queryKey: ['app.icon.list', serial, pkg, versionCode],
    queryFn: async () => {
      const out = await adbApi.run<IconListResult>({
        serial,
        name: 'app.icon.list',
        args: { package: pkg, apkPath, versionCode },
      });
      return out.result;
    },
    staleTime: Infinity,
    gcTime: 30 * 60 * 1000,
    retry: false,
  });

  /**
   * Final candidate list shown in the grid. `primary` (if available)
   * is pinned to slot 0 so the user always sees the Apps-list icon
   * first; the rest come from `app.icon.list` already sorted by the
   * backend (manifest first, then width/density desc).
   */
  const candidates = useMemo<IconCandidate[]>(() => {
    const out: IconCandidate[] = [];
    if (primary) out.push(primary);
    const listed = list.data?.icons ?? [];
    for (const c of listed) {
      // Should never collide with PRIMARY_RES_PATH but guard anyway.
      if (c.resPath === PRIMARY_RES_PATH) continue;
      out.push(c);
    }
    return out;
  }, [primary, list.data]);

  // Default selection picks `primary` whenever we have it — that's
  // exactly what the user clicked on. When the cache is cold we fall
  // back to the backend's preferred entry (first manifest > primaryResPath).
  useEffect(() => {
    if (selectedPath) return;
    if (primary) {
      setSelectedPath(PRIMARY_RES_PATH);
      return;
    }
    if (!list.data) return;
    const firstManifest = list.data.icons.find((i) => i.source === 'manifest');
    const def =
      firstManifest?.resPath ?? list.data.primaryResPath ?? list.data.icons[0]?.resPath;
    if (def) setSelectedPath(def);
  }, [primary, list.data, selectedPath]);

  const selected = useMemo(() => {
    if (!selectedPath) return null;
    return candidates.find((c) => c.resPath === selectedPath) ?? null;
  }, [candidates, selectedPath]);

  // Esc closes (matches every other modal in the SPA).
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleCopy = async (): Promise<void> => {
    if (!selected) return;
    try {
      const blob = base64ToBlob(selected.pngBase64, 'image/png');
      await copyImageToClipboard(blob);
      setCopied(true);
      const dims = selected.width > 0 ? `${selected.width}×${selected.height} ` : '';
      onToast(`已复制 ${dims}图片到剪贴板`);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      onToast(`复制失败：${e instanceof Error ? e.message : String(e)}`);
    }
  };

  const handleDownload = (): void => {
    if (!selected) return;
    const blob = base64ToBlob(selected.pngBase64, 'image/png');
    const densityTag = selected.density || (selected.source === 'primary' ? 'primary' : 'default');
    const dims = selected.width > 0 ? `_${selected.width}x${selected.height}` : '';
    const filename = `${pkg}_${densityTag}${dims}.png`;
    downloadBlob(blob, filename);
    onToast(`已下载 ${filename}`);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="card-pad w-[680px] max-w-[95vw] space-y-3"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="truncate font-medium text-ink">
              {label || pkg}
              {label && label !== pkg && (
                <span className="ml-2 font-mono text-[11px] text-ink-dim">{pkg}</span>
              )}
            </div>
            {selected && (
              <div className="mt-0.5 text-[11px] text-ink-dim">
                {selected.width > 0
                  ? `${selected.width}×${selected.height} · `
                  : '尺寸未知 · '}
                {selected.density || (selected.source === 'primary' ? '主图' : '默认密度')} ·{' '}
                {(selected.bytes / 1024).toFixed(1)} KB
              </div>
            )}
          </div>
          <button type="button" className="btn-ghost" onClick={onClose} aria-label="关闭">
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[260px_1fr]">
          {/* Large preview */}
          <div className="flex h-64 items-center justify-center rounded-md border border-line bg-bg-soft p-3">
            {selected ? (
              <img
                src={`data:image/png;base64,${selected.pngBase64}`}
                alt={selected.resPath}
                className="max-h-full max-w-full object-contain"
                style={{ imageRendering: 'pixelated' }}
              />
            ) : list.isLoading ? (
              <Loader2 className="h-6 w-6 animate-spin text-ink-dim" />
            ) : list.error ? (
              <div className="text-center text-[12px] text-rose-600">
                加载失败：{(list.error as Error).message}
              </div>
            ) : (
              <div className="text-[12px] text-ink-dim">没有可用的图标</div>
            )}
          </div>

          {/* Candidate grid */}
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <div className="text-[11px] uppercase tracking-wide text-ink-dim">
                候选 ({candidates.length})
              </div>
              {list.isFetching && !list.data && (
                <span className="inline-flex items-center gap-1 text-[11px] text-ink-dim">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  正在解析 APK…
                </span>
              )}
            </div>
            {candidates.length === 0 ? (
              <div className="rounded-md border border-line bg-bg-soft p-3 text-[12px] text-ink-dim">
                {list.isLoading
                  ? '正在解析 APK 中的 launcher 图标…'
                  : '此 APK 没有可识别的 launcher PNG（可能是 adaptive icon 只用 vector，或资源用了非标命名）。'}
              </div>
            ) : (
              <div className="grid grid-cols-4 gap-2">
                {candidates.map((c) => (
                  <button
                    key={c.resPath}
                    type="button"
                    onClick={() => setSelectedPath(c.resPath)}
                    className={clsx(
                      'relative flex flex-col items-center gap-1 rounded-md border p-1.5 text-[10px] transition',
                      selectedPath === c.resPath
                        ? 'border-accent bg-accent-soft/40 ring-1 ring-accent/40'
                        : 'border-line hover:bg-bg-soft'
                    )}
                    title={
                      c.source === 'primary'
                        ? '主图（与应用列表相同）'
                        : `${c.resPath}\nsource: ${c.source ?? 'scan'}`
                    }
                  >
                    <SourceBadge source={c.source} />
                    <img
                      src={`data:image/png;base64,${c.pngBase64}`}
                      alt=""
                      className="h-10 w-10 object-contain"
                    />
                    <span className="font-mono text-ink-dim">
                      {c.width > 0 ? `${c.width}×${c.height}` : '主图'}
                    </span>
                    <span className="truncate text-ink-muted">
                      {c.density || (c.source === 'primary' ? '—' : '—')}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {selected && selected.source !== 'primary' && (
          <div
            className="truncate rounded-md bg-bg-soft px-2 py-1.5 font-mono text-[11px] text-ink-dim"
            title={selected.resPath}
          >
            {selected.resPath}
          </div>
        )}

        <footer className="flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            className="btn"
            disabled={!selected}
            onClick={handleCopy}
            title="将选中图标复制到系统剪贴板"
          >
            {copied ? (
              <Check className="h-3.5 w-3.5 text-emerald-500" />
            ) : (
              <Copy className="h-3.5 w-3.5" />
            )}
            {copied ? '已复制' : '复制图片'}
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={!selected}
            onClick={handleDownload}
          >
            <Download className="h-3.5 w-3.5" />
            下载 PNG
          </button>
        </footer>
      </div>
    </div>
  );
}

/**
 * Tiny corner badge identifying the candidate's origin. Three values:
 *   - 主图: synthesised from the Apps-list `app.icon` cache (shown
 *     immediately when the modal opens, no APK fetch required).
 *   - 清单: declared by AndroidManifest.xml + resources.arsc.
 *   - 扫描: name-pattern fallback (only when manifest unresolvable).
 */
function SourceBadge({ source }: { source?: 'primary' | 'manifest' | 'scan' }) {
  if (source === 'primary') {
    return (
      <span className="absolute right-0.5 top-0.5 rounded bg-emerald-500/15 px-1 py-px text-[9px] font-medium leading-none text-emerald-600">
        主图
      </span>
    );
  }
  if (source === 'manifest') {
    return (
      <span className="absolute right-0.5 top-0.5 rounded bg-accent/15 px-1 py-px text-[9px] font-medium leading-none text-accent">
        清单
      </span>
    );
  }
  return (
    <span className="absolute right-0.5 top-0.5 rounded bg-ink-dim/15 px-1 py-px text-[9px] font-medium leading-none text-ink-dim">
      扫描
    </span>
  );
}
